<script>
	// game-builder/SoundContext.svelte — [소리] 탭.
	//
	// 저장 모양(서버 SOUNDS 컬럼 / 런타임 GAME/src/types.ts SoundConfig):
	//   { slots: { bgmMain: assetId, … }, bgmVolume, sfxVolume, vibrate }
	//
	// ⚠️ `slots` 에는 **assetId(ref)** 가 들어간다. URL 을 넣으면 음원을 교체해도 안 바뀐다
	//    (그림 슬롯과 같은 규약 — 썸네일용 URL 과 저장용 ref 를 끝까지 분리한다).
	// ⚠️ 볼륨·진동은 **안 정했으면 키를 안 넣는다.** 기본값은 런타임 config.ts 한 곳이 갖고,
	//    여기서 저장해 두면 나중에 우리가 기본을 바꿔도 그 저작자만 옛 값에 묶인다
	//    ([난이도] 탭 resetOne 과 같은 생각).
	import { createEventDispatcher } from "svelte";
	import { Section, Switch } from "../../lib/ui";
	import SoundPicker from "./SoundPicker.svelte";
	import { botAssetFolder } from "../chat-builder/imageAssetApi.js";
	import {
		SOUND_GROUPS,
		SOUND_VOLUMES,
		SOUND_VIBRATE,
		builtinSoundOf,
		isBuiltinSoundRef,
	} from "./gameSlots.js";

	export let bot;
	/** 서버 SOUNDS 한 덩어리(저작용 ref). */
	export let sound = {};
	/** assetId → URL. 미리듣기용(저장하지 않는다). */
	export let urls = {};

	const dispatch = createEventDispatcher();

	let pickerOpen = false;
	let pickerSlot = null;
	/** 피커에 넘길 종류 — 배경음악 칸에서 효과음 목록을 보여줄 이유가 없다. */
	let pickerKind = null;
	/** 지금 미리듣는 슬롯. 피커 밖(탭 화면)에서도 들어볼 수 있어야 한다. */
	let playingSlot = null;
	let audio = null;

	$: slots = sound?.slots || {};

	const emit = (next) => dispatch("change", next);

	/** 슬롯 하나만 갈아끼운다 — 통째로 덮으면 볼륨·진동이 날아간다. */
	const setSlot = (slotKey, assetId) =>
		emit({ ...sound, slots: { ...slots, [slotKey]: assetId } });

	const clearSlot = (slotKey) => {
		const nextSlots = { ...slots };
		delete nextSlots[slotKey];
		stop();
		emit({ ...sound, slots: nextSlots });
	};

	const onPick = (e) => {
		const { assetId, url } = e.detail;
		pickerOpen = false;
		if (!pickerSlot) return;
		// 미리듣기용 URL 은 부모가 들고 있는 assetId→URL 맵에 넣어둔다(저장값은 ref 쪽에만).
		dispatch("assetUrl", { assetId, url });
		setSlot(pickerSlot, assetId);
		pickerSlot = null;
	};

	const volumeOf = (v) => {
		const raw = sound?.[v.key];
		return typeof raw === "number" && Number.isFinite(raw) ? raw : v.def;
	};
	const setVolume = (v, raw) => {
		const n = Number(raw);
		if (!Number.isFinite(n)) return;
		emit({ ...sound, [v.key]: Math.min(v.max, Math.max(v.min, n)) });
	};
	const isVolumeChanged = (v) => sound?.[v.key] !== undefined && sound[v.key] !== v.def;
	const resetVolume = (v) => {
		const next = { ...sound };
		delete next[v.key];
		emit(next);
	};

	$: vibrate = typeof sound?.vibrate === "boolean" ? sound.vibrate : SOUND_VIBRATE.def;
	const setVibrate = (on) => emit({ ...sound, vibrate: on });

	/**
	 * 탭에서 바로 들어보기.
	 *
	 * ⚠️⚠️ `crossOrigin` 을 **src 보다 먼저** 걸어야 한다. 빠뜨리면 브라우저가 Origin 없는 응답을
	 *    캐시에 넣고, 게임(Phaser)이 같은 URL 을 XHR 로 받을 때 그 캐시 때문에 **CORS 로 막힌다**
	 *    — 그림 `<img crossorigin>` 에서 실측으로 당한 함정과 같은 것이다.
	 * ⚠️ 볼륨도 저작값으로 맞춰 들려준다. 100% 로 들려주면 "게임에서는 왜 작지"가 된다.
	 */
	function preview(slotKey, isBgm) {
		if (playingSlot === slotKey) return stop();
		stop();
		const url = urlOf(slots[slotKey]);
		if (!url) return;
		const el = new Audio();
		el.crossOrigin = "anonymous";
		el.src = url;
		el.volume = volumeOf(SOUND_VOLUMES[isBgm ? 0 : 1]);
		el.onended = () => (playingSlot = null);
		el.onerror = () => (playingSlot = null);
		audio = el;
		playingSlot = slotKey;
		el.play().catch(() => (playingSlot = null));
	}

	function stop() {
		if (audio) {
			audio.pause();
			audio.src = "";
			audio = null;
		}
		playingSlot = null;
	}

	const openPicker = (slotKey, kind) => {
		stop();
		pickerSlot = slotKey;
		pickerKind = kind;
		pickerOpen = true;
	};

	/**
	 * 슬롯에 들어 있는 음원의 **URL**.
	 *
	 * ⚠️ 기본 제공은 ref 에서 **바로** URL 을 얻는다(코드 표에 있다). 서버가 내려준 맵에만
	 *    기대면, 저장 직후처럼 아직 그 맵이 안 채워진 순간에 미리듣기가 죽은 버튼이 된다.
	 */
	const urlOf = (ref) => (isBuiltinSoundRef(ref) ? builtinSoundOf(ref)?.url : urls[ref]);

	/** 슬롯에 **무엇이** 들어 있는지 — 이름이 없으면 저작자는 어떤 소리를 넣었는지 알 수 없다. */
	const nameOf = (ref) =>
		isBuiltinSoundRef(ref) ? (builtinSoundOf(ref)?.label ?? "기본 제공") : "직접 올린 음원";
</script>

{#each SOUND_GROUPS as group (group.title)}
	<Section title={group.title} description={group.desc}>
		<div class="tw-flex tw-flex-col tw-gap-2">
			{#each group.slots as slot (slot.key)}
				{@const assetId = slots[slot.key]}
				{@const url = urlOf(assetId)}
				<div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-border tw-px-3 tw-py-2.5">
					<div class="tw-min-w-[150px] tw-flex-1">
						<div class="tw-text-sm tw-font-semibold tw-text-foreground">{slot.label}</div>
						{#if slot.hint}
							<div class="tw-text-[11px] tw-leading-tight tw-text-muted-foreground">{slot.hint}</div>
						{/if}
					</div>

					{#if assetId}
						<!-- URL 이 아직 안 온 경우(다른 탭에서 방금 바뀌었거나 자산이 지워진 경우)에도
						     "들어 있다"는 사실은 보여야 한다 — 비면 저작자는 설정이 날아간 줄 안다. -->
						<button
							type="button"
							class="tw-h-8 tw-w-8 tw-shrink-0 tw-cursor-pointer tw-rounded-full tw-border tw-border-border tw-bg-muted tw-text-xs tw-text-foreground tw-disabled:tw-opacity-40"
							disabled={!url}
							title={url ? (playingSlot === slot.key ? "멈춤" : "들어보기") : "음원 주소를 불러오지 못했습니다"}
							aria-label="들어보기"
							on:click={() => preview(slot.key, !!group.bgm)}>{playingSlot === slot.key ? "■" : "▶"}</button
						>
						<span class="tw-max-w-[150px] tw-shrink-0 tw-truncate tw-text-[11px] tw-text-muted-foreground">
							{nameOf(assetId)}
						</span>
						<button
							type="button"
							class="tw-h-8 tw-shrink-0 tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-transparent tw-px-3 tw-text-xs tw-text-foreground"
							on:click={() => openPicker(slot.key, group.bgm ? "bgm" : "sfx")}>바꾸기</button
						>
						<button
							type="button"
							class="tw-h-8 tw-shrink-0 tw-cursor-pointer tw-rounded-md tw-border-0 tw-bg-transparent tw-px-1 tw-text-xs tw-text-muted-foreground tw-underline"
							on:click={() => clearSlot(slot.key)}>비우기</button
						>
					{:else}
						<button
							type="button"
							class="tw-h-8 tw-shrink-0 tw-cursor-pointer tw-rounded-md tw-bg-primary tw-px-3.5 tw-text-xs tw-font-medium tw-text-primary-foreground"
							on:click={() => openPicker(slot.key, group.bgm ? "bgm" : "sfx")}>소리 고르기</button
						>
					{/if}
				</div>
			{/each}
		</div>
	</Section>
{/each}

<Section
	title="볼륨 · 진동"
	description="플레이어는 메인 화면 오른쪽 위의 스피커 아이콘으로 소리를 통째로 끌 수 있습니다(플레이 중에는 멈춤 팝업에서)."
>
	<div class="tw-flex tw-flex-col tw-gap-5">
		{#each SOUND_VOLUMES as v (v.key)}
			<div>
				<div class="tw-mb-2 tw-flex tw-items-center tw-justify-between tw-gap-2">
					<span class="tw-text-sm tw-font-semibold tw-text-foreground">{v.label}</span>
					<span class="tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-font-bold tw-text-primary">
						{Math.round(volumeOf(v) * 100)}%
						{#if isVolumeChanged(v)}
							<button
								type="button"
								class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-[11px] tw-font-normal tw-text-muted-foreground tw-underline"
								on:click={() => resetVolume(v)}>되돌리기</button
							>
						{/if}
					</span>
				</div>
				<input
					class="tw-w-full"
					style="accent-color: hsl(var(--sb-primary))"
					type="range"
					min={v.min}
					max={v.max}
					step={v.step}
					value={volumeOf(v)}
					on:change={(e) => setVolume(v, e.target.value)}
				/>
			</div>
		{/each}

		<div class="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-border-t tw-border-border tw-pt-4">
			<div class="tw-min-w-0">
				<div class="tw-text-sm tw-font-semibold tw-text-foreground">{SOUND_VIBRATE.label}</div>
				<div class="tw-text-[11px] tw-leading-relaxed tw-text-muted-foreground">{SOUND_VIBRATE.hint}</div>
			</div>
			<!-- ⚠️ Switch 는 `on:change` 가 아니라 **`on:click` 을 포워딩**한다(상태는 부모가 소유).
			     on:change 로 걸면 아무 일도 안 일어난다 — Account.svelte 와 같은 관용구를 쓴다. -->
			<Switch
				checked={vibrate}
				on:click={() => setVibrate(!vibrate)}
				aria-label={SOUND_VIBRATE.label}
			/>
		</div>
	</div>

	<p class="tw-mt-5 tw-rounded-lg tw-border tw-border-border tw-bg-muted/40 tw-p-4 tw-text-[13px] tw-leading-relaxed tw-text-muted-foreground">
		⚠️ 브라우저는 <strong class="tw-text-foreground">사용자가 화면을 한 번 만지기 전에는 소리를 내지 않습니다.</strong>
		그래서 게임을 열자마자는 음악이 안 들리고, 아무 곳이나 한 번 누르면 그때부터 흐릅니다. 고장이 아닙니다.
	</p>
</Section>

<SoundPicker
	bind:open={pickerOpen}
	chatId={bot.id}
	folderPath={botAssetFolder(bot)}
	kindFilter={pickerKind}
	currentAssetId={pickerSlot ? slots[pickerSlot] : null}
	on:select={onPick}
/>
